import { useState } from 'react'
import type { JSX } from 'react'

import {
  ChannelIcon,
  Checkbox,
  Flex,
  Heading,
  Notice,
} from '@buffer-mono/popcorn'
import { isChannelType } from '~publish/helpers/channels'

import type { StepProps } from '../../types'
import { StepActions } from '../StepActions/StepActions'

import styles from './ChannelSelectStep.module.css'

export function ChannelSelectStep({
  step,
  initialAnswers,
  disabled,
  loading,
  error,
  onDismissError,
  onSubmit,
  onSkip,
}: StepProps): JSX.Element {
  const [selected, setSelected] = useState<string[]>(initialAnswers)

  const toggle = (slug: string): void => {
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    )
  }

  return (
    <div className={styles.step}>
      <Flex direction="column" gap="xs" align="center">
        <Heading as="h1" size="xl" align="center">
          {step.title}
        </Heading>
      </Flex>

      {error && (
        <Notice variant="error" onDismiss={onDismissError}>
          <Notice.Text>{error}</Notice.Text>
        </Notice>
      )}

      <div className={styles.options}>
        {step.options.map((option) => {
          const isSelected = selected.includes(option.slug)
          return (
            <label
              key={option.slug}
              className={styles.card}
              data-selected={isSelected || undefined}
            >
              <input
                type="checkbox"
                className={styles.hiddenInput}
                checked={isSelected}
                onChange={(): void => toggle(option.slug)}
                aria-label={option.label}
                disabled={disabled}
              />
              <Checkbox
                checked={isSelected}
                className={styles.checkbox}
                tabIndex={-1}
                aria-hidden="true"
                disabled={disabled}
              />
              {isChannelType(option.slug) && (
                <ChannelIcon
                  type={option.slug}
                  size="medium"
                  className={styles.channelIcon}
                />
              )}
              <div className={styles.cardContent}>
                <Heading as="h2" size="sm" align="center">
                  {option.label}
                </Heading>
              </div>
            </label>
          )
        })}
      </div>

      <StepActions
        onContinue={(): void => onSubmit(selected)}
        disabled={selected.length === 0}
        controlsDisabled={disabled}
        loading={loading}
        isSkippable={step.isSkippable}
        onSkip={onSkip}
      />
    </div>
  )
}
